"use client"

import { router } from "@inertiajs/react"
import {
  AlertCircle,
  Link01,
  Plus,
  RefreshCw01,
  XClose,
} from "@untitledui/icons"
import { Reorder, useDragControls, useReducedMotion } from "motion/react"
import {
  type PointerEvent,
  type ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"

import { Button } from "@/components/ui/button"
import { ButtonUtility } from "@/components/ui/button-utility"
import { InputBase, TextField } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"
import { isUsable, lookupToTrackPayload, lookupUrl } from "@/lib/content-lookup"
import { cn } from "@/lib/utils"
import {
  movePlaylistTrackPath,
  playlistTrackPath,
  playlistTracksPath,
} from "@/routes"
import type { Tag, WarmStartClaim, WarmStartFieldNote } from "@/types"
import type { LookupResult } from "@/types/contents"

import {
  type MarginNoteEditor,
  MarginNotes,
  MarginRow,
  type NoteAnchor,
  marginRowClass,
} from "./claim-margin"
import {
  type ClaimTrack,
  ClaimTrackCard,
  type TrackPersistParams,
} from "./claim-track-card"

export interface ClaimTrackBoardHandle {
  /** Commit any pending remove and resolve once all queued edits persist. */
  flush: () => Promise<void>
  /**
   * Run a non-track visit through the same serialized persistence queue —
   * Inertia cancels concurrent visits, so anything that persists from this
   * editor must line up behind the board's mutations.
   */
  enqueue: (run: (done: () => void) => void) => void
}

interface ClaimTrackBoardProps {
  playlist: { id: number; slug: string | null }
  /**
   * The warm-start claim. Present in the "Make it yours" / coordinator editors,
   * where each row carries a growth-manager margin note. Absent (null) in a
   * normal owner/admin edit — then the board is a plain single column.
   */
  claim?: WarmStartClaim | null
  tracks: ClaimTrack[]
  /** Selectable system tags for each track's edit slideout. */
  availableTags: Tag[]
  /** Notifies the editor when the board is empty (publish needs ≥1 track). */
  onEmptyChange?: (empty: boolean) => void
  /** How mutations authorise and return — recipient token or coordinator id. */
  persistParams: TrackPersistParams
  /** Board title — "Your tracks" for the recipient; named for the coordinator. */
  heading?: string
  /**
   * Builds the margin-note persistence handlers for a track anchor. Present
   * only on the coordinator surface (notes are authored there, read here).
   */
  noteEditorFor?: (anchor: NoteAnchor) => MarginNoteEditor
}

interface PendingAdd {
  key: string
  status: "loading" | "ready" | "error"
  url: string
  result?: LookupResult | null
}

export const ClaimTrackBoard = forwardRef<
  ClaimTrackBoardHandle,
  ClaimTrackBoardProps
>(function ClaimTrackBoard(
  {
    playlist,
    claim,
    tracks,
    availableTags,
    onEmptyChange,
    persistParams,
    heading = "Your tracks",
    noteEditorFor,
  },
  ref,
) {
  // Persist by immutable id in coordinator mode: a words save can rename the
  // slug mid-session; the recipient keeps the slug URLs their flow already uses.
  const pid =
    "warm_start" in persistParams ? playlist.id : (playlist.slug ?? playlist.id)
  const reduce = useReducedMotion()

  // Claim editors carry a margin-note column beside every row; a normal owner
  // edit has no second party, so its rows are a single column (PlainRow).
  const hasMargin = claim != null
  const Row = hasMargin ? MarginRow : PlainRow

  // --- State -------------------------------------------------------------
  // `order` is the optimistic source of truth; it reconciles with the server
  // `tracks` prop only when no mutation is in flight (see below), so a burst
  // of edits isn't clobbered mid-flight by an intermediate reload.
  const [order, setOrder] = useState<ClaimTrack[]>(tracks)
  const [pending, setPending] = useState(0)
  const [pendingDeletes, setPendingDeletes] = useState<Set<number>>(new Set())
  const [removed, setRemoved] = useState<ClaimTrack | null>(null)
  const [pendingAdds, setPendingAdds] = useState<PendingAdd[]>([])
  const [draggingId, setDraggingId] = useState<number | null>(null)
  const [addValue, setAddValue] = useState("")

  const [prevTracks, setPrevTracks] = useState(tracks)
  const removeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const addKey = useRef(0)
  const addInputRef = useRef<HTMLInputElement>(null)

  // Reconcile with the authoritative server list once idle, using the
  // documented "adjust state when a prop changes" pattern (a state snapshot of
  // the last-seen prop). The reseed drops resolved add placeholders (their real
  // tracks have arrived) and snaps the order to what persisted — optimistic and
  // server states are the same shape, so there's no visible jump.
  if (tracks !== prevTracks && pending === 0) {
    setPrevTracks(tracks)
    setOrder(tracks)
    setPendingAdds((prev) => prev.filter((p) => p.status !== "ready"))
  }

  const visible = order.filter((t) => !pendingDeletes.has(t.id))
  const isEmpty = visible.length === 0 && pendingAdds.length === 0
  useEffect(() => {
    onEmptyChange?.(isEmpty)
  }, [isEmpty, onEmptyChange])

  // --- Serialized persistence -------------------------------------------
  // Inertia cancels an in-flight visit when a new one starts, so we chain the
  // track mutations through a single promise queue. Optimistic UI updates
  // happen immediately; the queue just persists them in order, in the
  // background, each landing back in this same editor (claim_return_path).
  const queue = useRef<Promise<void>>(Promise.resolve())
  const enqueue = (run: (done: () => void) => void) => {
    setPending((p) => p + 1)
    queue.current = queue.current
      .then(() => new Promise<void>((resolve) => run(resolve)))
      .catch(() => undefined)
      .finally(() => setPending((p) => p - 1))
  }

  const persistMove = (id: number, position: number) =>
    enqueue((done) =>
      router.patch(
        movePlaylistTrackPath(pid, id, { ...persistParams }),
        { position },
        { preserveScroll: true, preserveState: true, onFinish: done },
      ),
    )

  const persistNote = (id: number, notes: string) =>
    enqueue((done) =>
      router.patch(
        playlistTrackPath(pid, id, { ...persistParams }),
        { track: { notes } },
        { preserveScroll: true, preserveState: true, onFinish: done },
      ),
    )

  const persistDelete = (id: number) =>
    enqueue((done) =>
      router.delete(playlistTrackPath(pid, id, { ...persistParams }), {
        preserveScroll: true,
        preserveState: true,
        onFinish: done,
      }),
    )

  const persistAdd = (result: LookupResult, url: string) =>
    enqueue((done) =>
      router.post(
        playlistTracksPath(pid, { ...persistParams }),
        { track: lookupToTrackPayload(result, url) },
        { preserveScroll: true, preserveState: true, onFinish: done },
      ),
    )

  // --- Reorder -----------------------------------------------------------
  const handleReorder = (next: ClaimTrack[]) => setOrder(next)

  const handleDragStart = (id: number) => {
    commitRemoved() // settle any grace-window remove so positions line up
    setDraggingId(id)
  }

  const handleDragEnd = (id: number) => {
    setDraggingId(null)
    const index = order.findIndex((t) => t.id === id)
    if (index !== -1) persistMove(id, index + 1)
  }

  // --- Inline note -------------------------------------------------------
  const handleNoteChange = (id: number, notes: string) =>
    setOrder((prev) => prev.map((t) => (t.id === id ? { ...t, notes } : t)))

  const handleNoteCommit = (id: number) => {
    const current = order.find((t) => t.id === id)?.notes ?? ""
    const saved = tracks.find((t) => t.id === id)?.notes ?? ""
    if (current.trim() !== saved.trim()) persistNote(id, current)
  }

  // --- Remove (forgiving, grace-window deferred delete) ------------------
  const commitRemoved = () => {
    if (removeTimer.current) {
      clearTimeout(removeTimer.current)
      removeTimer.current = null
    }
    setRemoved((r) => {
      if (r) persistDelete(r.id)
      return null
    })
  }

  const handleRemove = (track: ClaimTrack) => {
    commitRemoved() // only one undo at a time; settle the previous
    setPendingDeletes((prev) => new Set(prev).add(track.id))
    setRemoved(track)
    removeTimer.current = setTimeout(commitRemoved, 6000)
  }

  // Repost swap: a not-owned post can't be rewritten (it's shared), so
  // "replace" removes this track and hands the recipient the add-a-link input
  // to point at a source of their own. The undo strip still covers the remove.
  const handleReplace = (track: ClaimTrack) => {
    handleRemove(track)
    addInputRef.current?.focus()
  }

  const handleUndo = () => {
    if (removeTimer.current) {
      clearTimeout(removeTimer.current)
      removeTimer.current = null
    }
    setRemoved((r) => {
      if (r) {
        setPendingDeletes((prev) => {
          const next = new Set(prev)
          next.delete(r.id)
          return next
        })
      }
      return null
    })
  }

  // --- Add via paste -----------------------------------------------------
  const handleAdd = (raw: string) => {
    const url = raw.trim()
    if (!url) return
    commitRemoved()
    const key = `add-${addKey.current++}`
    setPendingAdds((prev) => [...prev, { key, status: "loading", url }])
    setAddValue("")
    void lookupUrl(url).then((result) => {
      if (isUsable(result)) {
        setPendingAdds((prev) =>
          prev.map((p) =>
            p.key === key ? { ...p, status: "ready", result } : p,
          ),
        )
        persistAdd(result, url)
      } else {
        setPendingAdds((prev) =>
          prev.map((p) =>
            p.key === key ? { ...p, status: "error", result } : p,
          ),
        )
      }
    })
  }

  const retryAdd = (key: string, url: string) => {
    setPendingAdds((prev) =>
      prev.map((p) => (p.key === key ? { ...p, status: "loading" } : p)),
    )
    void lookupUrl(url).then((result) => {
      if (isUsable(result)) {
        setPendingAdds((prev) =>
          prev.map((p) =>
            p.key === key ? { ...p, status: "ready", result } : p,
          ),
        )
        persistAdd(result, url)
      } else {
        setPendingAdds((prev) =>
          prev.map((p) =>
            p.key === key ? { ...p, status: "error", result } : p,
          ),
        )
      }
    })
  }

  const dismissAdd = (key: string) =>
    setPendingAdds((prev) => prev.filter((p) => p.key !== key))

  // --- Publish flush + shared queue access --------------------------------
  useImperativeHandle(ref, () => ({
    flush: async () => {
      commitRemoved()
      await queue.current
    },
    enqueue,
  }))

  const marginNotesFor = (track: ClaimTrack): WarmStartFieldNote[] =>
    claim?.field_notes.filter(
      (n) =>
        n.anchor.field === "track" && n.anchor.ref === String(track.post.id),
    ) ?? []

  return (
    <section
      className="flex flex-col gap-3"
      aria-label="Tracks in this playlist"
    >
      <Row>
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="text-primary text-lg font-semibold">{heading}</h2>
          <p className="text-tertiary text-sm">
            Drag to reorder, tweak a note, or drop in a link.
          </p>
        </div>
      </Row>

      <Reorder.Group
        as="div"
        axis="y"
        values={visible}
        onReorder={handleReorder}
        className="flex flex-col gap-3"
      >
        {visible.map((track, i) => (
          <ClaimTrackItem
            key={track.id}
            track={track}
            index={i + 1}
            hasMargin={hasMargin}
            authorName={claim?.created_by_name ?? ""}
            avatarUrl={claim?.created_by_avatar_url}
            marginNotes={marginNotesFor(track)}
            noteEditor={noteEditorFor?.({
              field: "track",
              ref: String(track.post.id),
            })}
            isDragging={draggingId === track.id}
            animateLayout={!reduce}
            pid={pid}
            persistParams={persistParams}
            availableTags={availableTags}
            onNoteChange={(v) => handleNoteChange(track.id, v)}
            onNoteCommit={() => handleNoteCommit(track.id)}
            onRemove={() => handleRemove(track)}
            onReplace={() => handleReplace(track)}
            onDragStart={() => handleDragStart(track.id)}
            onDragEnd={() => handleDragEnd(track.id)}
          />
        ))}
      </Reorder.Group>

      {/* Nascent cards while a pasted link resolves — in the playlist column. */}
      {pendingAdds.map((add) => (
        <Row key={add.key}>
          <PendingAddCard
            add={add}
            onRetry={() => retryAdd(add.key, add.url)}
            onDismiss={() => dismissAdd(add.key)}
          />
        </Row>
      ))}

      {/* Add-via-link affordance — lives in the playlist column. */}
      <Row>
        <div className="border-secondary bg-secondary/40 flex items-center gap-2 rounded-xl border border-dashed p-2 pl-3">
          <Link01 aria-hidden className="text-quaternary size-5 shrink-0" />
          <TextField
            aria-label="Paste a link to add a track"
            value={addValue}
            onChange={setAddValue}
            className="flex-1"
          >
            <InputBase
              ref={addInputRef}
              size="sm"
              placeholder="Paste a link to add a track…"
              className="border-0 bg-transparent shadow-none ring-0 focus:ring-0"
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  handleAdd(addValue)
                }
              }}
              onPaste={(e: React.ClipboardEvent) => {
                const text = e.clipboardData.getData("text")
                if (/^https?:\/\//i.test(text.trim())) {
                  e.preventDefault()
                  handleAdd(text)
                }
              }}
            />
          </TextField>
          <Button
            type="button"
            size="sm"
            color="secondary"
            iconLeading={Plus}
            aria-label="Add track"
            isDisabled={!addValue.trim()}
            onClick={() => handleAdd(addValue)}
          />
        </div>
      </Row>

      {isEmpty && (
        <Row>
          <p className="text-tertiary py-2 text-sm">
            {hasMargin
              ? "Add at least one track before you publish."
              : "No tracks yet. Paste a link above to add the first one."}
          </p>
        </Row>
      )}

      {/* Forgiving undo strip — the delete only fires once this clears. */}
      {removed && (
        <Row>
          <div className="border-secondary bg-primary flex items-center justify-between gap-3 rounded-xl border px-4 py-3 shadow-xs">
            <p className="text-secondary truncate text-sm">
              Removed{" "}
              <span className="text-primary font-medium">
                {removed.post.title}
              </span>
            </p>
            <Button
              type="button"
              size="sm"
              color="link-color"
              onClick={handleUndo}
            >
              Undo
            </Button>
          </div>
        </Row>
      )}
    </section>
  )
})

// A single-column row for the normal owner/admin board (no margin-note column).
// A stable module-level component so switching layouts doesn't remount rows.
function PlainRow({ children }: { children: ReactNode; aside?: ReactNode }) {
  return <div className="min-w-0">{children}</div>
}

// ---------------------------------------------------------------------------
// One reorderable item: owns its drag controls so only the handle initiates a
// drag (the note textarea stays freely selectable).
// ---------------------------------------------------------------------------
interface ClaimTrackItemProps {
  track: ClaimTrack
  index: number
  /** Claim editors show a margin note beside each card; a normal edit doesn't. */
  hasMargin: boolean
  authorName: string
  avatarUrl?: string | null
  marginNotes: WarmStartFieldNote[]
  noteEditor?: MarginNoteEditor
  isDragging: boolean
  animateLayout: boolean
  pid: number | string
  persistParams: TrackPersistParams
  availableTags: Tag[]
  onNoteChange: (value: string) => void
  onNoteCommit: () => void
  onRemove: () => void
  onReplace: () => void
  onDragStart: () => void
  onDragEnd: () => void
}

function ClaimTrackItem({
  track,
  index,
  hasMargin,
  authorName,
  avatarUrl,
  marginNotes,
  noteEditor,
  isDragging,
  animateLayout,
  pid,
  persistParams,
  availableTags,
  onNoteChange,
  onNoteCommit,
  onRemove,
  onReplace,
  onDragStart,
  onDragEnd,
}: ClaimTrackItemProps) {
  const controls = useDragControls()

  const card = (
    <ClaimTrackCard
      track={track}
      index={index}
      note={track.notes ?? ""}
      pid={pid}
      persistParams={persistParams}
      availableTags={availableTags}
      onNoteChange={onNoteChange}
      onNoteCommit={onNoteCommit}
      onRemove={onRemove}
      onReplace={onReplace}
      isDragging={isDragging}
      onHandlePointerDown={(e: PointerEvent) => controls.start(e)}
    />
  )

  return (
    <Reorder.Item
      as="div"
      value={track}
      dragListener={false}
      dragControls={controls}
      layout={animateLayout ? "position" : undefined}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={cn(hasMargin && marginRowClass, isDragging && "relative z-10")}
    >
      {hasMargin ? (
        <>
          {/* Playlist column — the card (with its own inline note). */}
          <div className="min-w-0">{card}</div>
          {/* Margin — the coordinator's note for this track, travelling with it. */}
          <div className="lg:self-start">
            <MarginNotes
              notes={marginNotes}
              authorName={authorName}
              avatarUrl={avatarUrl}
              currentValue={track.notes ?? ""}
              editor={noteEditor}
            />
          </div>
        </>
      ) : (
        card
      )}
    </Reorder.Item>
  )
}

// ---------------------------------------------------------------------------
// Placeholder card shown while a pasted link resolves (and its error state).
// ---------------------------------------------------------------------------
function PendingAddCard({
  add,
  onRetry,
  onDismiss,
}: {
  add: PendingAdd
  onRetry: () => void
  onDismiss: () => void
}) {
  if (add.status === "error") {
    return (
      <div className="border-secondary bg-primary flex items-center gap-3 rounded-xl border border-dashed p-4">
        <AlertCircle
          aria-hidden
          className="text-warning-primary size-5 shrink-0"
        />
        <div className="min-w-0 flex-1">
          <p className="text-secondary text-sm font-medium">
            We couldn&apos;t reach that link
          </p>
          <p className="text-tertiary truncate text-xs">{add.url}</p>
        </div>
        <Button
          type="button"
          size="sm"
          color="secondary"
          iconLeading={RefreshCw01}
          onClick={onRetry}
        >
          Try again
        </Button>
        <ButtonUtility
          size="xs"
          color="tertiary"
          tooltip="Dismiss"
          icon={XClose}
          onClick={onDismiss}
        />
      </div>
    )
  }

  const fields = add.result?.fields
  return (
    <div className="border-secondary bg-primary flex items-center gap-4 rounded-xl border p-4 shadow-xs">
      <div className="bg-secondary_subtle relative size-14 shrink-0 overflow-hidden rounded-lg sm:size-16">
        {fields?.thumbnail_url ? (
          <img
            src={fields.thumbnail_url}
            alt=""
            className="size-full object-cover"
          />
        ) : null}
        <div className="bg-primary/40 absolute inset-0 flex items-center justify-center">
          <Spinner className="size-5" />
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-primary truncate font-medium">
          {fields?.title ?? "Fetching link…"}
        </p>
        <p className="text-tertiary truncate text-sm">{add.url}</p>
      </div>
    </div>
  )
}
